import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ScanFace, Sparkles, ShieldCheck } from 'lucide-react';
import { LoginContext } from '../../MainLayout/MainLayout';
import goodLightingIcon from '../../Assets/Images/rewamp/skin/good lighting.svg';
import faceCenteredIcon from '../../Assets/Images/rewamp/skin/face centered.svg';
import noMakeupIcon from '../../Assets/Images/rewamp/skin/no makeup.svg';
import neutralExpressionIcon from '../../Assets/Images/rewamp/skin/neutral expression.svg';
import poweredByAiIcon from '../../Assets/Images/rewamp/skin/powered by AI.svg';
import viewPastReportIcon from '../../Assets/Images/rewamp/skin/view past report button.svg';
import fullRightImage from '../../Assets/Images/rewamp/skin/full right side image.webp';

const instructionCards = [
  { icon: goodLightingIcon, title: 'Good Lighting', desc: 'Use natural light, avoid shadows' },
  { icon: faceCenteredIcon, title: 'Face Centered', desc: 'Keep your face clearly visible' },
  { icon: noMakeupIcon, title: 'No Heavy Makeup', desc: 'Ensure accurate skin analysis' },
  { icon: neutralExpressionIcon, title: 'Neutral Expression', desc: 'Avoid filters or extreme angles' },
];

const whyFeatures = [
  { Icon: ScanFace, title: 'AI-Powered Analysis', desc: 'Advanced AI scans, multiple skin attributes' },
  { Icon: Sparkles, title: 'Personalized Insights', desc: 'Get a routine tailored to your unique skin' },
  { Icon: ShieldCheck, title: 'Data Security', desc: 'We keep your data safe and private' },
];

const CosmeticSkinCareLanding = () => {
  const navigate = useNavigate();
  const { openLogin } = useContext(LoginContext);

  const isLoggedIn = () => {
    try {
      const loginObj = JSON.parse(localStorage.getItem('loginObj') || 'null');
      return Boolean(loginObj?.code || loginObj?.patientId);
    } catch {
      return false;
    }
  };

  const handleStartAnalysis = () => {
    if (!isLoggedIn()) {
      openLogin();
      return;
    }
    // Starting a brand-new scan — clear any leftover images/requestId from a
    // previous session so they don't leak into this one.
    try {
      sessionStorage.removeItem('cosmeticFaceImages');
      sessionStorage.removeItem('cosmeticRequestId');
      sessionStorage.removeItem('cosmeticResultsReturnPath');
    } catch {}
    navigate('/cosmetic-skincare-analysis/face-scan');
  };

  const handleViewPastReports = () => {
    if (!isLoggedIn()) {
      openLogin();
      return;
    }
    navigate('/cosmetic-skincare-analysis/reports');
  };

  return (
    <div className="mx-auto w-full">
      <style>{`
        .cosmetic-wrapper {
          background-color: #fff;
        }

        .cosmetic-content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          align-items: stretch;
        }

        .cosmetic-left {
          display: flex;
          flex-direction: column;
          /* left gutter grows on very wide screens so the text lines up with the 1440px frame */
          padding: 28px 48px 40px max(64px, calc(50vw - 656px));
          background: #ffffff;
        }

        .cosmetic-back {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          font-weight: 600;
          font-size: 15px;
          color: #1A1A2E;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          margin-bottom: 36px;
          align-self: flex-start;
        }

        .cosmetic-heading {
          font-family: 'Inter', sans-serif;
          font-weight: 700;
          font-size: 40px;
          line-height: 50px;
          color: var(--color-brand-primary);
          margin-bottom: 18px;
        }

        .cosmetic-heading-accent {
          display: block;
          color: var(--color-brand-green);
        }

        .cosmetic-desc {
          font-family: 'Inter', sans-serif;
          font-weight: 400;
          font-size: 19px;
          line-height: 30px;
          color: #3A3A3A;
          max-width: 600px;
          margin-bottom: 32px;
        }

        .cosmetic-hero-steps.cosmetic-instr-grid {
          margin: 0 0 40px 0;
          max-width: 640px;
          gap: 18px;
        }

        .cosmetic-cta-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 18px;
          max-width: 640px;
          margin-bottom: 28px;
        }

        .cosmetic-cta-col {
          display: flex;
          flex-direction: column;
          align-items: stretch;
          gap: 8px;
        }

        .cosmetic-btn-primary {
          width: 100%;
          font-weight: 600;
          font-size: 15px;
          color: #ffffff;
          background: var(--color-brand-primary);
          border: 1.5px solid var(--color-brand-primary);
          border-radius: 999px;
          padding: 12px 26px;
          cursor: pointer;
          transition: background 0.2s;
        }
        .cosmetic-btn-primary:hover { background: var(--color-brand-primary-hover); }

        .cosmetic-btn-outline {
          width: 100%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-weight: 600;
          font-size: 15px;
          color: var(--color-brand-primary);
          background: #ffffff;
          border: 1.5px solid var(--color-brand-primary);
          border-radius: 999px;
          padding: 12px 26px;
          cursor: pointer;
          transition: background 0.2s;
        }
        .cosmetic-btn-outline:hover { background: #EAF3FA; }

        .cosmetic-cta-caption {
          font-size: 13px;
          color: #676C80;
          text-align: center;
        }

        .cosmetic-footer-note {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          max-width: 640px;
          background: #EEF6FC;
          border: 1px solid #DCEEF9;
          border-radius: 10px;
          padding: 14px 16px;
          font-size: 14px;
          color: var(--color-brand-primary);
        }

        /* Right half: the image already contains the face, region labels and the
           three feature cards. Panel colour matches the image's own background. */
        .cosmetic-right {
          position: relative;
          background: #D5EAF8;
          overflow: hidden;
        }

        /* Sized to the left column's height (not the panel's width) so the whole
           image — face and feature cards — stays on screen beside the text. */
        .cosmetic-right-image {
          position: absolute;
          inset: 24px 24px 0 24px;
          width: calc(100% - 48px);
          height: calc(100% - 24px);
          object-fit: contain;
          object-position: center bottom;
        }

        /* Mobile-only sections ("Why Cosmetic Skin Care" + a relocated "Check Your
           Skin in 4 Steps") that replace the in-hero steps grid on small screens. */
        .cosmetic-mobile-sections {
          display: none;
        }

        .cosmetic-section {
          max-width: 1320px;
          margin: 48px auto 0 auto;
        }

        .cosmetic-section-heading {
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 26px;
          line-height: 34px;
          text-align: center;
          color: var(--color-brand-primary);
          margin-bottom: 6px;
        }

        .cosmetic-section-subtext {
          font-size: 14px;
          text-align: center;
          color: #676C80;
          max-width: 560px;
          margin: 0 auto 24px auto;
        }

        .cosmetic-why-grid {
          display: flex;
          flex-direction: column;
          gap: 14px;
          max-width: 720px;
          margin: 0 auto;
        }

        .cosmetic-why-card {
          display: flex;
          align-items: center;
          gap: 14px;
          background: #ffffff;
          border: 1px solid #C9EEF8;
          border-radius: 12px;
          padding: 14px 18px;
          box-shadow: 0px 2px 6px 0px #00000010;
        }

        .cosmetic-why-icon {
          flex-shrink: 0;
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: var(--color-brand-primary);
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .cosmetic-why-title {
          font-weight: 600;
          font-size: 15px;
          color: var(--color-brand-primary);
        }

        .cosmetic-why-desc {
          font-size: 13px;
          color: var(--color-text-subheading);
        }

        .cosmetic-instr-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
          max-width: 720px;
          margin: 0 auto;
        }

        .cosmetic-instr-card {
          display: flex;
          align-items: center;
          gap: 16px;
          background: #ffffff;
          border: 1px solid #C9EEF8;
          border-radius: 12px;
          padding: 18px 20px;
          box-shadow: 0px 4px 10px 0px #80DAF140;
        }

        .cosmetic-instr-icon {
          flex-shrink: 0;
          width: 48px;
          height: 48px;
        }

        .cosmetic-instr-title {
          font-weight: 600;
          font-size: 15px;
          color: var(--color-brand-primary);
          margin-bottom: 2px;
        }

        .cosmetic-instr-desc {
          font-size: 13px;
          line-height: 1.4;
          color: #676C80;
        }

        @media (max-width: 1024px) {
          .cosmetic-left { padding: 24px 28px 32px 30px; }
          .cosmetic-heading { font-size: 32px; line-height: 42px; }
          .cosmetic-desc { font-size: 17px; line-height: 26px; }
          .cosmetic-cta-row { grid-template-columns: 1fr; }
        }

        @media (max-width: 768px) {
          .cosmetic-content { grid-template-columns: 1fr; }
          .cosmetic-left { padding: 20px 20px 28px 20px; align-items: center; }
          .cosmetic-back { margin-bottom: 20px; }
          .cosmetic-heading { font-size: 28px; line-height: 36px; text-align: center; }
          .cosmetic-desc { font-size: 16px; line-height: 24px; text-align: center; }
          .cosmetic-cta-row { width: 100%; grid-template-columns: 1fr 1fr; }
          .cosmetic-footer-note { width: 100%; }
          .cosmetic-right { display: flex; justify-content: center; padding-top: 16px; }
          .cosmetic-right-image { position: static; width: 100%; max-width: 420px; height: auto; }
          .cosmetic-hero-steps { display: none; }
          .cosmetic-mobile-sections { display: block; padding: 0 20px 32px 20px; }
          .cosmetic-section { margin-top: 36px; }
          .cosmetic-section-heading { font-size: 22px; line-height: 28px; }
        }

        @media (max-width: 480px) {
          .cosmetic-heading { font-size: 24px; line-height: 32px; }
          .cosmetic-desc { font-size: 15px; line-height: 22px; }
          .cosmetic-instr-grid { grid-template-columns: 1fr; }
          .cosmetic-cta-row { grid-template-columns: 1fr; gap: 16px; }
          .cosmetic-why-card { padding: 12px 14px; }
        }
      `}</style>

      <div className="cosmetic-wrapper">
        <div className="cosmetic-content">
          {/* LEFT */}
          <div className="cosmetic-left">
            <button className="cosmetic-back" onClick={() => navigate(-1)}>
              <ChevronLeft size={20} />
              Cosmetic Skincare Analysis
            </button>

            <h1 className="cosmetic-heading">
              Let's analyze your skin
              <span className="cosmetic-heading-accent">for a natural glow</span>
            </h1>
            <p className="cosmetic-desc">
              Upload your face image to get AI-powered skincare insights, routine, and personalized recommendations.
            </p>

            <div className="cosmetic-hero-steps cosmetic-instr-grid">
              {instructionCards.map(({ icon, title, desc }) => (
                <div className="cosmetic-instr-card" key={title}>
                  <img src={icon} alt="" className="cosmetic-instr-icon" />
                  <div>
                    <div className="cosmetic-instr-title">{title}</div>
                    <div className="cosmetic-instr-desc">{desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="cosmetic-cta-row">
              <div className="cosmetic-cta-col">
                <button className="cosmetic-btn-primary" onClick={handleStartAnalysis}>
                  Start Analysis
                </button>
                <span className="cosmetic-cta-caption">Takes less than 30 Seconds</span>
              </div>

              <div className="cosmetic-cta-col">
                <button className="cosmetic-btn-outline" onClick={handleViewPastReports}>
                  <img src={viewPastReportIcon} alt="" width={14} height={17} />
                  View My Past Reports
                </button>
                <span className="cosmetic-cta-caption">Want to see your previous reports?</span>
              </div>
            </div>

            <div className="cosmetic-footer-note">
              <img src={poweredByAiIcon} alt="" width={14} height={17} />
              Powered By AI trained on millions of skin images
            </div>
          </div>

          {/* RIGHT */}
          <div className="cosmetic-right">
            <img src={fullRightImage} alt="Cosmetic skin analysis" className="cosmetic-right-image" />
          </div>
        </div>

        {/* Mobile-only — desktop already shows the steps inline in the hero above
            and has no separate "why" section (matches the original desktop design). */}
        <div className="cosmetic-mobile-sections">
          {/* WHY COSMETIC SKIN CARE */}
          <div className="cosmetic-section">
            <h2 className="cosmetic-section-heading">Why CureBay's Cosmetic Skin Care</h2>
            <p className="cosmetic-section-subtext">Powered by AI trained on millions of skin images</p>
            <div className="cosmetic-why-grid">
              {whyFeatures.map(({ Icon, title, desc }) => (
                <div className="cosmetic-why-card" key={title}>
                  <span className="cosmetic-why-icon">
                    <Icon size={20} />
                  </span>
                  <div>
                    <div className="cosmetic-why-title">{title}</div>
                    <div className="cosmetic-why-desc">{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CHECK YOUR SKIN IN 4 STEPS */}
          <div className="cosmetic-section">
            <h2 className="cosmetic-section-heading">Check Your Skin in Just 4 Steps</h2>
            <p className="cosmetic-section-subtext">Follow four quick steps to get instant AI-powered analysis for healthier, clearer skin</p>
            <div className="cosmetic-instr-grid">
              {instructionCards.map(({ icon, title, desc }) => (
                <div className="cosmetic-instr-card" key={title}>
                  <img src={icon} alt="" className="cosmetic-instr-icon" />
                  <div>
                    <div className="cosmetic-instr-title">{title}</div>
                    <div className="cosmetic-instr-desc">{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CosmeticSkinCareLanding;
